import React from "react";

// Match either URLs or email addresses (URLs first to avoid @ in URLs matching email)
const LINK_REGEX = /(https?:\/\/[^\s]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

// Trim trailing sentence punctuation that was captured after the URL or email (e.g. "), " or ").")
function trimTrailingPunctuation(str) {
  return str.replace(/[)\]}"',.;:!?]+$/, "");
}

// Workstream heading lines are rendered bold
const WORKSTREAM_HEADING_REGEX = /^Workstream \d+:/;
// "Action Required :" lines are rendered bold, same size as first line (1.1em)
const ACTION_REQUIRED_HEADING_REGEX = /^Action Required\s*:/;
// Workstreams intro line is rendered bold
const WORKSTREAMS_INTRO_REGEX = /^As indicated previously, our recovery efforts are currently focused across three key workstreams:/;
// "Yes" (straight or curly quotes) is rendered bold
const YES_BOLD_REGEX = /(["\u201C\u201D]Yes["\u201C\u201D])/g;
// Line that gets half-line space above and below
const SECURING_CONFIRMATIONS_LINE_REGEX = /^Securing confirmations in advance will help expedite/;

/**
 * Splits message text and renders URLs as clickable links and emails as mailto links.
 * Links use stopPropagation so clicking them doesn't trigger parent div's onClick.
 */
export function messageWithLinks(text) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(LINK_REGEX).filter(Boolean);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const url = trimTrailingPunctuation(part);
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="message-link"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
      );
    }
    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(part.trim())) {
      const email = trimTrailingPunctuation(part);
      return (
        <a
          key={i}
          href={`mailto:${email}`}
          className="message-link"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

/**
 * Renders message as static rich text: newlines as line breaks,
 * URLs as clickable links, emails as mailto links, "Workstream N: ..." lines as bold,
 * and quoted "Yes" (straight or curly quotes) as bold.
 */
export function renderRichMessage(text) {
  if (!text || typeof text !== "string") return null;
  const lines = text.split(/\r?\n/);
  return lines.map((line, lineIndex) => {
    let content;
    const yesParts = line.split(YES_BOLD_REGEX);
    if (yesParts.length > 1) {
      content = (
        <>
          {yesParts.map((part, i) =>
            i % 2 === 1 ? (
              <strong key={i}>{part}</strong>
            ) : (
              <React.Fragment key={i}>{messageWithLinks(part)}</React.Fragment>
            )
          )}
        </>
      );
    } else {
      content = messageWithLinks(line);
    }
    const isBoldHeading =
      WORKSTREAM_HEADING_REGEX.test(line.trim()) ||
      WORKSTREAMS_INTRO_REGEX.test(line.trim());
    const isActionRequiredLine = ACTION_REQUIRED_HEADING_REGEX.test(line.trim());
    const hasContentBefore = lines.slice(0, lineIndex).some((l) => l.trim() !== "");
    const isSecuringConfirmationsLine = SECURING_CONFIRMATIONS_LINE_REGEX.test(line.trim());
    const prevLineIsSecuringConfirmations = lineIndex > 0 && SECURING_CONFIRMATIONS_LINE_REGEX.test(lines[lineIndex - 1].trim());
    // Action Required: same size as first line (1.1em); other headings: fontWeight 600, lineHeight 30px
    const boldStyle = isActionRequiredLine
      ? { fontWeight: 600, fontSize: '1.1em' }
      : { fontWeight: 600, lineHeight: '30px' };
    const lineContent = (isBoldHeading || isActionRequiredLine) ? <strong style={boldStyle}>{content}</strong> : content;
    return (
      <React.Fragment key={lineIndex}>
        {lineIndex > 0 && hasContentBefore && !prevLineIsSecuringConfirmations && <br />}
        {isSecuringConfirmationsLine ? (
          <span style={{ display: 'block', marginTop: '0.5em', marginBottom: '0.5em' }}>{lineContent}</span>
        ) : lineContent}
      </React.Fragment>
    );
  });
}
